import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, FileText, Building2, MapPin,
  Briefcase, Loader, Users, Hash, Phone, Mail, Clock
} from 'lucide-react';
import { AnnonceDetail as AnnonceDetailType } from '../data/annonces';
import { getAnnonceDetail } from '../api/services/AnnonceService';
import './AnnonceDetail.css';

// --- Utilitaires de rendu du contenu ---

// Caractères de puces possibles dans les textes d'annonces
const BULLET_CHARS = ['⎫', '⎬', '⎭', '•', '►', '▸', '▪', '■', '→'];
const BULLET_REGEX = new RegExp(`^[${BULLET_CHARS.join('')}\\-–—\\*]\\s*`);

// Détecte si une ligne est un titre (courte, en majuscules)
const isHeadingLine = (line: string): boolean => {
  const trimmed = line.trim();
  if (trimmed.length < 5 || trimmed.length > 120) return false;
  const upperCount = (trimmed.match(/[A-ZÀ-ÖÙ-Ý]/g) || []).length;
  const letterCount = (trimmed.match(/[a-zA-ZÀ-ÿ]/g) || []).length;
  if (letterCount > 0 && upperCount / letterCount > 0.7 && !trimmed.endsWith('.') && !trimmed.endsWith(',')) {
    return true;
  }
  return false;
};

const isBulletLine = (line: string): boolean => BULLET_REGEX.test(line.trim());
const cleanBullet = (line: string): string => line.trim().replace(BULLET_REGEX, '');

// Détecte si le texte est un bloc continu sans vrais sauts de ligne (format v1_fratmat)
const isSingleLineText = (text: string): boolean => {
  const lines = text.split('\n').filter(l => l.trim());
  // Si une seule ligne ou si la ligne la plus longue dépasse 300 chars → texte continu
  return lines.length <= 1 || (lines.length <= 3 && lines.some(l => l.length > 300));
};

// Pré-traite un texte continu v1_fratmat pour y injecter des sauts de ligne
const preprocessSingleLine = (text: string): string => {
  let result = text;

  // Insérer un saut avant les items numérotés : "1)", "2)", etc.
  result = result.replace(/\s+(\d+\))/g, '\n$1');

  // Insérer un saut avant les séquences de mots majuscules (3+ mots consécutifs en CAPS)
  // ex: "REVOCATION DE GERANT" ou "AUGMENTATION DE CAPITAL SOCIAL"
  result = result.replace(
    /\s+((?:[A-ZÀ-ÖÙ-Ý][A-ZÀ-ÖÙ-Ý']+(?:\s+|$)){3,})/g,
    '\n$1'
  );

  // Insérer un saut avant "Aux termes", "Par décision", "Par jugement", "Il résulte", "Par délibération"
  result = result.replace(
    /\s+(Aux termes|Par décision|Par jugement|Il résulte|Par délibération|Suivant|En vertu)/g,
    '\n$1'
  );

  // Insérer un saut avant "Pour avis" / "POUR AVIS"
  result = result.replace(/\s+(Pour avis|POUR AVIS|Le Notaire|LE NOTAIRE|Le Greffier)/gi, '\n$1');

  return result;
};

// Rend le contenu texte avec une mise en forme structurée
const renderContenu = (text: string): React.ReactNode[] => {
  // Si texte continu (v1), le pré-traiter pour ajouter de la structure
  const processedText = isSingleLineText(text) ? preprocessSingleLine(text) : text;

  const lines = processedText.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    // Items numérotés : "1)...", "2)..." → liste ordonnée
    if (/^\d+\)/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\)/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\)\s*/, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="contenu-list contenu-list-ordered">
          {items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Groupe de lignes à puces consécutives → liste
    if (isBulletLine(line)) {
      const items: string[] = [];
      while (i < lines.length && isBulletLine(lines[i].trim())) {
        items.push(cleanBullet(lines[i]));
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} className="contenu-list">
          {items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ligne titre (majuscules)
    if (isHeadingLine(line)) {
      elements.push(
        <h4 key={`h-${i}`} className="contenu-heading">{line}</h4>
      );
      i++;
      continue;
    }

    // Paragraphe normal
    const paragraphLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !isBulletLine(lines[i].trim()) &&
      !isHeadingLine(lines[i].trim()) &&
      !/^\d+\)/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim());
      i++;
    }
    elements.push(
      <p key={`p-${i}`}>{paragraphLines.join('\n')}</p>
    );
  }

  return elements;
};

const AnnonceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState<AnnonceDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (id) {
          const response = await getAnnonceDetail(id);
          const data = response.data;
          if (data.success) {
            setAnnonce(data as AnnonceDetailType);
          } else {
            setError(data.message || 'Annonce introuvable');
          }
        }
      } catch (err: any) {
        const message = err?.response?.data?.message || 'Erreur lors du chargement';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  const getBadgeClass = (): string => {
    const type = (annonce?.type_annonce || '').toLowerCase();
    if (type.includes('constitution') || type.includes('création')) return 'badge-creation';
    if (type.includes('modification') || type.includes('augmentation') || type.includes('cession')) return 'badge-modification';
    if (type.includes('dissolution')) return 'badge-dissolution';
    if (type.includes('liquidation') || type.includes('cloture')) return 'badge-liquidation';
    return 'badge-creation';
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return 'Non disponible';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCapital = (capital?: number | null, devise?: string): string => {
    if (!capital) return '';
    const formatted = capital.toLocaleString('fr-FR');
    return `${formatted} ${devise === 'XOF' ? 'FCFA' : devise || 'FCFA'}`;
  };

  if (isLoading) {
    return (
      <div className="detail-page">
        <div className="detail-container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <Loader size={40} className="spin" />
          <p style={{ marginTop: '16px', color: '#888' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !annonce) {
    return (
      <div className="detail-not-found">
        <h2>{error || 'Annonce introuvable'}</h2>
        <Link to="/recherche">Retour à la recherche</Link>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="detail-container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Retour aux résultats
        </button>

        <div className="detail-layout">
          {/* Contenu principal */}
          <div className="detail-main">
            <div className="detail-card">
              <span className={`badge ${getBadgeClass()}`}>
                {annonce.type_annonce || 'Annonce'}
              </span>
              <h1 className="detail-title">{annonce.nom_societe}</h1>
              <p className="detail-company-line">
                {annonce.nom_societe}
                {annonce.rccm && ` — RCCM: ${annonce.rccm}`}
              </p>

              {/* Modifications listées */}
              {annonce.modifications && annonce.modifications.length > 0 && (
                <div className="detail-modifications">
                  <h3>Modifications</h3>
                  <ul>
                    {annonce.modifications.map((mod, i) => (
                      <li key={i}>{mod.description}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contenu complet */}
              {annonce.contenu_complet && (
                <div className="detail-full-text">
                  {renderContenu(annonce.contenu_complet)}
                </div>
              )}

              <div className="detail-meta-row">
                <span><Calendar size={14} /> Publié le {formatDate(annonce.date_publication)}</span>
                {annonce.date_depot && (
                  <span><FileText size={14} /> Déposé le {formatDate(annonce.date_depot)}</span>
                )}
                {annonce.date_pv && (
                  <span><FileText size={14} /> PV du {formatDate(annonce.date_pv)}</span>
                )}
                <span>
                  <FileText size={14} />
                  {annonce.version === 'v1_fratmat' ? 'Fraternité Matin' : 'Journal Officiel'}
                </span>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="detail-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-header">
                <Building2 size={18} color="#E85D04" />
                <h3>Informations entreprise</h3>
              </div>

              <div className="sidebar-info-row">
                <span className="info-label">Dénomination</span>
                <span className="info-value info-value-bold">{annonce.nom_societe}</span>
              </div>

              {annonce.rccm && (
                <div className="sidebar-info-row">
                  <span className="info-label">RCCM</span>
                  <span className="info-value info-value-orange">{annonce.rccm}</span>
                </div>
              )}

              <div className="sidebar-info-2col">
                {annonce.forme_juridique && (
                  <div>
                    <span className="info-label">Forme juridique</span>
                    <span className="info-value">{annonce.forme_juridique}</span>
                  </div>
                )}
                {annonce.numero_publication && (
                  <div>
                    <span className="info-label">N° Publication</span>
                    <span className="info-value">{annonce.numero_publication}</span>
                  </div>
                )}
              </div>

              {annonce.duree && annonce.duree !== 'None' && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <Clock size={15} color="#888" />
                    <div>
                      <span className="info-label">Durée</span>
                      <span className="info-value">{annonce.duree} ans</span>
                    </div>
                  </div>
                </div>
              )}

              {annonce.capital_social != null && annonce.capital_social > 0 && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <FileText size={15} color="#888" />
                    <div>
                      <span className="info-label">Capital social</span>
                      <span className="info-value info-value-bold">
                        {formatCapital(annonce.capital_social, annonce.devise)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {annonce.siege_social && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <MapPin size={15} color="#888" />
                    <div>
                      <span className="info-label">Siège social</span>
                      <span className="info-value">{annonce.siege_social}</span>
                    </div>
                  </div>
                </div>
              )}

              {(annonce.activite_principale || annonce.activite_secondaire || annonce.objet_social) && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <Briefcase size={15} color="#888" />
                    <div>
                      <span className="info-label">Activité / Objet social</span>
                      <span className="info-value">
                        {annonce.activite_principale || annonce.activite_secondaire || annonce.objet_social}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {annonce.gerant && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <Users size={15} color="#888" />
                    <div>
                      <span className="info-label">Gérant</span>
                      <span className="info-value">
                        {annonce.gerant_civilite ? `${annonce.gerant_civilite} ` : ''}{annonce.gerant}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {annonce.numero_greffe && (
                <div className="sidebar-info-row">
                  <div className="info-with-icon">
                    <Hash size={15} color="#888" />
                    <div>
                      <span className="info-label">N° Greffe</span>
                      <span className="info-value">{annonce.numero_greffe}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Demandeur */}
              {annonce.demandeur && annonce.demandeur.noms && (
                <>
                  <div className="sidebar-separator" />
                  <div className="sidebar-header">
                    <Users size={18} color="#E85D04" />
                    <h3>Demandeur</h3>
                  </div>
                  <div className="sidebar-info-row">
                    <span className="info-value">{annonce.demandeur.noms}</span>
                  </div>
                  {annonce.demandeur.adresse && (
                    <div className="sidebar-info-row">
                      <div className="info-with-icon">
                        <MapPin size={14} color="#888" />
                        <span className="info-value" style={{ fontSize: '13px' }}>{annonce.demandeur.adresse}</span>
                      </div>
                    </div>
                  )}
                  {annonce.demandeur.telephone && (
                    <div className="sidebar-info-row">
                      <div className="info-with-icon">
                        <Phone size={14} color="#888" />
                        <span className="info-value">{annonce.demandeur.telephone}</span>
                      </div>
                    </div>
                  )}
                  {annonce.demandeur.email && (
                    <div className="sidebar-info-row">
                      <div className="info-with-icon">
                        <Mail size={14} color="#888" />
                        <span className="info-value">{annonce.demandeur.email}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AnnonceDetailPage;
